import { useRef, useState, useCallback, useEffect } from 'react';
import { Camera, RefreshCw, ChevronLeft, ImagePlus } from 'lucide-react';
import { useI18n } from '@/contexts/i18n-hook';

const TARGET_WIDTH = 1920;
const TARGET_HEIGHT = 1080;
const TARGET_FRAME_RATE = 60;
const MIN_LONG_SIDE = 1280;
const MIN_SHORT_SIDE = 720;

interface CameraResolution {
    width: number;
    height: number;
}

interface CameraComponentProps {
    onCapture: (blob: Blob) => void;
    onCancel?: () => void;
}

export function CameraComponent({ onCapture, onCancel }: CameraComponentProps) {
    const { t } = useI18n();
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const [hasCameraError, setHasCameraError] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const [isResolutionInsufficient, setIsResolutionInsufficient] = useState(false);
    const [effectiveResolution, setEffectiveResolution] = useState<CameraResolution | null>(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const hiddenFileInputRef = useRef<HTMLInputElement>(null);

    const isAtLeast720p = (resolution: CameraResolution) => {
        const longSide = Math.max(resolution.width, resolution.height);
        const shortSide = Math.min(resolution.width, resolution.height);
        return longSide >= MIN_LONG_SIDE && shortSide >= MIN_SHORT_SIDE;
    };

    const waitForMetadata = (video: HTMLVideoElement) => {
        if (video.readyState >= 1 && video.videoWidth > 0 && video.videoHeight > 0) {
            return Promise.resolve();
        }

        return new Promise<void>((resolve) => {
            const onLoadedMetadata = () => {
                video.removeEventListener('loadedmetadata', onLoadedMetadata);
                resolve();
            };
            video.addEventListener('loadedmetadata', onLoadedMetadata);

            window.setTimeout(() => {
                video.removeEventListener('loadedmetadata', onLoadedMetadata);
                resolve();
            }, 1500);
        });
    };

    const detectStreamResolution = async (
        stream: MediaStream,
        videoElement: HTMLVideoElement | null
    ): Promise<CameraResolution | null> => {
        const [videoTrack] = stream.getVideoTracks();
        const settings = videoTrack?.getSettings();
        const settingsWidth = Number(settings?.width ?? 0);
        const settingsHeight = Number(settings?.height ?? 0);

        if (settingsWidth > 0 && settingsHeight > 0) {
            return {
                width: settingsWidth,
                height: settingsHeight
            };
        }

        if (!videoElement) {
            return null;
        }

        await waitForMetadata(videoElement);
        if (videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
            return {
                width: videoElement.videoWidth,
                height: videoElement.videoHeight
            };
        }

        return null;
    };

    const startCamera = useCallback(async () => {
        try {
            setIsInitializing(true);
            setHasCameraError(false);
            setIsResolutionInsufficient(false);
            setEffectiveResolution(null);

            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { ideal: 'environment' },
                    width: { ideal: TARGET_WIDTH },
                    height: { ideal: TARGET_HEIGHT },
                    aspectRatio: { ideal: TARGET_WIDTH / TARGET_HEIGHT },
                    frameRate: { ideal: TARGET_FRAME_RATE, min: 15 }
                },
                audio: false
            });

            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play().catch(() => undefined);
            }

            const resolution = await detectStreamResolution(stream, videoRef.current);
            if (resolution) {
                setEffectiveResolution(resolution);
                setIsResolutionInsufficient(!isAtLeast720p(resolution));
            }
        } catch (err) {
            console.error('Error accessing camera:', err);
            setHasCameraError(true);
        } finally {
            setIsInitializing(false);
        }
    }, []);

    useEffect(() => {
        startCamera();
        return () => {
            // Cleanup: stop camera when unmounting
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, [startCamera]);

    const takePhoto = () => {
        if (!videoRef.current || isResolutionInsufficient) return;

        setIsCapturing(true);

        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        const ctx = canvas.getContext('2d');

        if (ctx) {
            ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
                if (blob) onCapture(blob);
                setTimeout(() => setIsCapturing(false), 350);
            }, 'image/jpeg', 0.95);
        } else {
            setIsCapturing(false);
        }
    };

    const handleNativeCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            onCapture(file);
        }
        // Reset the input so the same file can be captured again if needed
        if (hiddenFileInputRef.current) {
            hiddenFileInputRef.current.value = '';
        }
    };

    if (hasCameraError) {
        return (
            <div className="bg-red-50 border-2 border-red-200 text-red-700 rounded-lg p-6 text-center">
                <p className="mb-4">{t('aquisicao.camera.erroPermissao')}</p>
                <button
                    onClick={startCamera}
                    className="px-4 py-2 bg-white text-red-700 border border-red-300 rounded hover:bg-red-50 inline-flex items-center"
                >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {t('comum.acao.tentarNovamente')}
                </button>
            </div>
        );
    }

    return (
        <div className="relative flex-1 w-full h-full bg-black overflow-hidden shadow-inner">
            {isCapturing && (
                <div className="absolute inset-0 bg-white animate-camera-flash pointer-events-none z-20" />
            )}

            {isInitializing && (
                <div className="absolute inset-0 flex items-center justify-center text-white">
                    <RefreshCw className="w-8 h-8 animate-spin" />
                </div>
            )}

            {isResolutionInsufficient && (
                <div className="absolute inset-x-3 top-3 z-10 rounded-lg border border-amber-300 bg-amber-100/95 px-3 py-2 text-xs text-amber-900 shadow">
                    <p className="font-semibold">{t('aquisicao.camera.qualidade.titulo')}</p>
                    <p className="mt-0.5">
                        {effectiveResolution
                            ? t('aquisicao.camera.qualidade.detectada', {
                                largura: effectiveResolution.width,
                                altura: effectiveResolution.height,
                            })
                            : t('aquisicao.camera.qualidade.semDeteccao')}
                    </p>
                </div>
            )}

            <video
                ref={videoRef}
                autoPlay
                playsInline
                className="absolute inset-0 w-full h-full object-contain"
            />

            {/* Overlay Actions */}
            <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 to-transparent flex items-center justify-between">
                {onCancel ? (
                    <button
                        type="button"
                        onClick={onCancel}
                        className="flex items-center px-4 py-2 bg-white/20 text-white rounded-full hover:bg-white/30 backdrop-blur-sm transition"
                    >
                        <ChevronLeft className="w-5 h-5 mr-1" />
                        <span className="font-semibold text-sm">{t('comum.acao.voltar')}</span>
                    </button>
                ) : <div className="w-24" />}

                <div className="flex flex-col items-center gap-2">
                    <button
                        type="button"
                        onClick={takePhoto}
                        disabled={isInitializing || isResolutionInsufficient}
                        className="w-16 h-16 bg-white rounded-full border-4 border-schmersal-gray flex items-center justify-center shadow-lg transform active:scale-95 transition hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={t('aquisicao.camera.capturarStreaming')}
                    >
                        <Camera className="w-6 h-6 text-schmersal-blue" />
                    </button>
                    <span className="text-[10px] text-white/80 font-semibold bg-black/40 px-2 py-0.5 rounded-full">
                        {isResolutionInsufficient ? t('aquisicao.camera.rapidoBloqueado') : t('aquisicao.camera.rapido')}
                    </span>
                </div>

                <div className="flex flex-col items-center gap-2">
                    <button
                        type="button"
                        onClick={() => hiddenFileInputRef.current?.click()}
                        className={`w-14 h-14 rounded-full border-4 flex items-center justify-center shadow-lg transform active:scale-95 transition hover:brightness-110 ${isResolutionInsufficient
                            ? 'bg-green-600 border-green-300 ring-4 ring-green-300/60'
                            : 'bg-schmersal-blue border-schmersal-blue-light'
                            }`}
                        title={t('aquisicao.camera.capturarNativa')}
                    >
                        <ImagePlus className="w-5 h-5 text-white" />
                    </button>
                    <span className="text-[10px] text-white/80 font-semibold bg-black/40 px-2 py-0.5 rounded-full">{t('aquisicao.camera.hdNativo')}</span>
                </div>

                {/* Hidden File Input for Native Camera Capture */}
                <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    ref={hiddenFileInputRef}
                    onChange={handleNativeCapture}
                    className="hidden"
                />
            </div>
        </div>
    );
}
